import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { OrgLink } from "~/components/OrgLink";

type CustomerScopedOrgLinkProps = HTMLAttributes<HTMLElement> & {
  href: string;
  customerViewInScope: boolean;
  children: ReactNode;
};

/** Link only when customer view scope passes; otherwise plain text (docs/AUTH_ROLES.md). */
const CustomerScopedOrgLink = forwardRef<HTMLElement, CustomerScopedOrgLinkProps>(function CustomerScopedOrgLink(
  { href, customerViewInScope, children, onClick, ...props },
  ref,
) {
  const setRef = (element: HTMLElement | null) => {
    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  };

  if (!customerViewInScope) {
    return (
      <span {...props} ref={setRef}>
        {children}
      </span>
    );
  }

  return (
    <OrgLink {...props} href={href} onClick={onClick} ref={setRef}>
      {children}
    </OrgLink>
  );
});

type CustomerNameLinkProps = HTMLAttributes<HTMLElement> & {
  customerId: string;
  customerViewInScope: boolean;
  children: ReactNode;
};

export const CustomerNameLink = forwardRef<HTMLElement, CustomerNameLinkProps>(function CustomerNameLink(
  { customerId, customerViewInScope, children, ...props },
  ref,
) {
  return (
    <CustomerScopedOrgLink {...props} href={`/customers/${customerId}`} customerViewInScope={customerViewInScope} ref={ref}>
      {children}
    </CustomerScopedOrgLink>
  );
});

type CustomerSubRouteLinkProps = {
  customerId: string;
  customerViewInScope: boolean;
  subPath: "contacts" | "addresses" | "locations";
  children: ReactNode;
};

export const CustomerSubRouteLink = ({ customerId, customerViewInScope, subPath, children }: CustomerSubRouteLinkProps) => (
  <CustomerScopedOrgLink href={`/customers/${customerId}/${subPath}`} customerViewInScope={customerViewInScope}>
    {children}
  </CustomerScopedOrgLink>
);
